import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Alert,
    CircularProgress,
    Divider,
    Stack
} from '@mui/material';
import {
    Add as AddIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Language as WebsiteIcon,
    LinkedIn as LinkedInIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Person as PersonIcon
} from '@mui/icons-material';
import api from '../services/api';

interface Lawyer {
    _id: string;
    name: string;
    email: string;
    phoneNumber?: string;
    websiteAddress?: string;
    linkedInAddress?: string;
}

const LawyerManagement: React.FC = () => {
    const [lawyers, setLawyers] = useState<Lawyer[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingLawyer, setEditingLawyer] = useState<Lawyer | null>(null);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        phoneNumber: '',
        websiteAddress: '',
        linkedInAddress: ''
    });

    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        fetchLawyers();
    }, []);

    const fetchLawyers = async () => {
        try {
            const response = await api.get<Lawyer[]>('/lawyers');
            setLawyers(response.data);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to fetch lawyers');
        } finally {
            setLoading(false);
        }
    };

    const validate = () => {
        const errors: Record<string, string> = {};
        if (!formData.name) errors.name = 'Name is required';
        if (!formData.email) errors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email is invalid';

        if (!editingLawyer && !formData.password) {
            errors.password = 'Password is required to create a new lawyer profile';
        } else if (!editingLawyer && formData.password.length < 6) {
            errors.password = 'Password must be at least 6 characters';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleOpenDialog = (lawyer?: Lawyer) => {
        if (lawyer) {
            setEditingLawyer(lawyer);
            setFormData({
                name: lawyer.name,
                email: lawyer.email,
                password: '', // Password is not editable
                phoneNumber: lawyer.phoneNumber || '',
                websiteAddress: lawyer.websiteAddress || '',
                linkedInAddress: lawyer.linkedInAddress || ''
            });
        } else {
            setEditingLawyer(null);
            setFormData({
                name: '',
                email: '',
                password: '',
                phoneNumber: '',
                websiteAddress: '',
                linkedInAddress: ''
            });
        }
        setFormErrors({});
        setOpenDialog(true);
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        try {
            if (editingLawyer) {
                await api.patch(`/lawyers/${editingLawyer._id}`, formData);
            } else {
                await api.post('/lawyers', formData);
            }
            setOpenDialog(false);
            fetchLawyers();
        } catch (err: any) {
            setError(err.response?.data?.error || 'Operation failed');
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to remove this lawyer from your profile? This will not delete the lawyer account, only your association with them.')) return;

        try {
            await api.delete(`/lawyers/${id}`);
            fetchLawyers();
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to delete lawyer');
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, background: 'linear-gradient(45deg, #1976d2 30%, #21cbf3 90%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    My Lawyers
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    sx={{ borderRadius: 2, px: 3 }}
                >
                    Add Lawyer
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            <Grid container spacing={3}>
                {lawyers.length > 0 ? (
                    lawyers.map((lawyer) => (
                        <Grid item xs={12} md={6} lg={4} key={lawyer._id}>
                            <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <PersonIcon color="primary" sx={{ mr: 1 }} />
                                            <Typography variant="h6" sx={{ fontWeight: 600 }}>{lawyer.name}</Typography>
                                        </Box>
                                        <Box>
                                            <IconButton size="small" onClick={() => handleOpenDialog(lawyer)}><EditIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleDelete(lawyer._id)}><DeleteIcon fontSize="small" /></IconButton>
                                        </Box>
                                    </Box>

                                    <Stack spacing={1.5}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <EmailIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                                            <Typography variant="body2">{lawyer.email}</Typography>
                                        </Box>
                                        {lawyer.phoneNumber && (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <PhoneIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                                                <Typography variant="body2">{lawyer.phoneNumber}</Typography>
                                            </Box>
                                        )}
                                        {lawyer.websiteAddress && (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <WebsiteIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                                                <Typography variant="body2" component="a" href={lawyer.websiteAddress} target="_blank" sx={{ color: 'primary.main', textDecoration: 'none' }}>
                                                    Website
                                                </Typography>
                                            </Box>
                                        )}
                                        {lawyer.linkedInAddress && (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <LinkedInIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                                                <Typography variant="body2" component="a" href={lawyer.linkedInAddress} target="_blank" sx={{ color: 'primary.main', textDecoration: 'none' }}>
                                                    LinkedIn
                                                </Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                ) : (
                    <Grid item xs={12}>
                        <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'background.paper', borderRadius: 4 }}>
                            <Typography color="text.secondary">No lawyers added yet. Add a lawyer to help sign and manage your wills.</Typography>
                        </Box>
                    </Grid>
                )}
            </Grid>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {editingLawyer ? 'Edit Lawyer Profile' : 'Add New Lawyer'}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {!editingLawyer && (
                            <Alert severity="info" sx={{ mb: 1 }}>
                                If a lawyer with this email already exists, they will be imported to your profile.
                            </Alert>
                        )}
                        <TextField
                            label="FullName"
                            fullWidth
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            error={!!formErrors.name}
                            helperText={formErrors.name}
                            required
                        />
                        <TextField
                            label="Email Address"
                            fullWidth
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            error={!!formErrors.email}
                            helperText={formErrors.email}
                            required
                            disabled={!!editingLawyer}
                        />
                        {!editingLawyer && (
                            <TextField
                                label="Initial Password"
                                type="password"
                                fullWidth
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                error={!!formErrors.password}
                                helperText={formErrors.password || 'Set a password for the lawyer to login.'}
                                required
                            />
                        )}
                        <Divider sx={{ my: 1 }}>Optional Information</Divider>
                        <TextField
                            label="Phone Number"
                            fullWidth
                            value={formData.phoneNumber}
                            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                        />
                        <TextField
                            label="Website URL"
                            fullWidth
                            value={formData.websiteAddress}
                            onChange={(e) => setFormData({ ...formData, websiteAddress: e.target.value })}
                        />
                        <TextField
                            label="LinkedIn Profile"
                            fullWidth
                            value={formData.linkedInAddress}
                            onChange={(e) => setFormData({ ...formData, linkedInAddress: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit}>
                        {editingLawyer ? 'Update' : 'Add Lawyer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LawyerManagement;
